import * as Prism from 'prismjs';
if (typeof window !== 'undefined' && typeof window.Prism === 'undefined') {
  /* istanbul ignore next */
  window.Prism = Prism;
}
import { createEditor, $getRoot } from 'lexical';
import type { RangeSelection, TextNode, ElementNode } from 'lexical';
import { $isAtNodeEnd } from '@lexical/selection';
import { urlRegExp, SUPPORTED_URL_PROTOCOLS } from '../constants/constants';
import { CHECK_LIST, TRANSFORMERS, $convertFromMarkdownString, type Transformer, type TextMatchTransformer } from '@lexical/markdown';
import { $createEquationNode, $isBlockEquationNode, $isEquationNode, $isInlineEquationNode, EquationNode } from '../nodes/EquationNode';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
export const getSelectedNode = (selection: RangeSelection): TextNode | ElementNode => {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  return $isAtNodeEnd(isBackward ? focus : anchor) ? anchorNode : focusNode;
};
export function validateUrl(url: string): boolean {
  return url === 'https://' || urlRegExp.test(url);
}
export function sanitizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return 'about:blank';
    }
  } catch {
    return url;
  } finally {
    return url;
  }
}
export const getTransformedMarkdown = (markdown: string): string => {
  return markdown.replace(/\$\$([^]+?)\$\$/g, match => match.split('\n').join(''));
};
export const EQUATION_INLINE: TextMatchTransformer = {
  dependencies: [EquationNode],
  export: node => {
    if (!$isEquationNode(node) || !$isInlineEquationNode(node)) {
      return null;
    }
    return `$${node.getEquation()}$`;
  },
  importRegExp: /(?<!\$)\$([^$]+?)\$(?!\$)/,
  regExp: /(?<!\$)\$([^$]+?)\$(?!\$)/,
  replace: (textNode, match) => {
    const [, equation] = match;
    const equationNode = $createEquationNode(equation, true);
    textNode.replace(equationNode);
  },
  trigger: '$',
  type: 'text-match'
};
export const EQUATION_BLOCK: TextMatchTransformer = {
  dependencies: [EquationNode],
  export: node => {
    if (!$isEquationNode(node) || !$isBlockEquationNode(node)) {
      return null;
    }
    return `\n$$${node.getEquation()}$$\n`;
  },
  importRegExp: /\$\$([^]+?)\$\$/,
  regExp: /\$\$([^]+?)\$\$/,
  replace: (textNode, match) => {
    const [, equation] = match;
    const equationNode = $createEquationNode(equation, false);
    textNode.replace(equationNode);
  },
  trigger: '$',
  type: 'text-match'
};
export const EDITOR_TRANSFORMER: Array<Transformer> = [EQUATION_BLOCK,
// Must be before EQUATION_INLINE to avoid inline formula detection in the block formula, e.g. $$ \text{$ inline formula $} $$
EQUATION_INLINE, CHECK_LIST, ...TRANSFORMERS];
export const EditorConfigNodes = [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, AutoLinkNode, LinkNode, EquationNode];
export const getRawTextFromMarkdown = (markdown: string) => {
  let text = '';
  const editor = createEditor({
    nodes: EditorConfigNodes
  });
  editor.update(() => {
    $convertFromMarkdownString(markdown, EDITOR_TRANSFORMER);
  }, {
    discrete: true
  });
  editor.getEditorState().read(() => {
    text = $getRoot().getTextContent().trim();
  });
  return text;
};